import { StripeInputComponent } from "@/features/subscription/components/StripeInputComponent";
import { caremeTheme } from "@/libs";
import { functions } from "@/libs/firebase";
import {
	Backdrop,
	Button,
	CircularProgress,
	FormLabel,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import {
	CardCvcElement,
	CardExpiryElement,
	CardNumberElement,
	useElements,
	useStripe,
} from "@stripe/react-stripe-js";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import type React from "react";
import { useCallback, useState } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";

export function CheckoutForm({
	clientSecret,
	priceId,
	buttonText,
}: {
	clientSecret: string;
	priceId: string;
	buttonText: string;
}) {
	const router = useRouter();
	const { enqueueSnackbar } = useSnackbar();

	const [createSubscriptionRequest] = useHttpsCallable<
		{ priceId: string; paymentMethodId: string },
		{ subscriptionId: string }
	>(functions, "createSubscription");

	const stripe = useStripe();
	const elements = useElements();
	const [submitting, setSubmitting] = useState(false);

	const showSnackbar = useCallback(
		(message: string, variant: "success" | "error") => {
			enqueueSnackbar(message, {
				variant: variant,
				anchorOrigin: {
					vertical: "top",
					horizontal: "center",
				},
			});
		},
		[enqueueSnackbar],
	);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		setSubmitting(true);

		event.preventDefault();

		const data = new FormData(event.currentTarget);
		const name = (data.get("name") || "") as string;

		if (!stripe || !elements) {
			return;
		}

		const cardNumber = elements.getElement(CardNumberElement);
		if (!cardNumber) {
			return;
		}

		try {
			const result = await stripe.confirmCardSetup(clientSecret, {
				payment_method: {
					card: cardNumber,
					billing_details: { name },
				},
			});

			if (!result.setupIntent) {
				showSnackbar("購入に失敗しました", "error");
				return;
			}

			if (result.error) {
				showSnackbar("購入に失敗しました", "error");
				return;
			}

			const subscriptionResult = await createSubscriptionRequest({
				priceId,
				paymentMethodId: result.setupIntent.payment_method?.toString() || "",
			});

			if (!subscriptionResult?.data.subscriptionId) {
				showSnackbar("購入に失敗しました", "error");
				return;
			}

			router.push("/home");
			showSnackbar("購入が完了しました", "success");
		} catch (e) {
			enqueueSnackbar("購入に失敗しました", {
				variant: "error",
				anchorOrigin: {
					vertical: "top",
					horizontal: "center",
				},
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<form onSubmit={handleSubmit}>
				<FormLabel sx={{ typography: "body2", mt: 2 }}>
					クレジットカード名義人
				</FormLabel>
				<TextField
					margin="normal"
					required
					fullWidth
					variant="standard"
					placeholder="HANAKO YAMADA"
					name="name"
					autoComplete="name"
					sx={{ mb: 4 }}
				/>

				<FormLabel sx={{ typography: "body2" }}>クレジットカード番号</FormLabel>
				<TextField
					margin="normal"
					required
					fullWidth
					variant="standard"
					name="card"
					InputProps={{
						inputComponent: StripeInputComponent,
						inputProps: {
							component: CardNumberElement,
							options: {
								placeholder: "1234 5678 9012 3456",
							},
						},
					}}
					sx={{ mb: 4 }}
				/>

				<Box mb={0.5} display="flex" gap={4}>
					<Box display={"flex"} flexDirection="column" flex={1}>
						<FormLabel sx={{ typography: "body2" }}>有効期限</FormLabel>
						<TextField
							margin="normal"
							required
							fullWidth
							variant="standard"
							name="expiry"
							InputProps={{
								inputComponent: StripeInputComponent,
								inputProps: { component: CardExpiryElement },
							}}
						/>
					</Box>

					<Box display={"flex"} flexDirection="column" flex={1}>
						<FormLabel sx={{ typography: "body2" }}>
							セキュリティコード
						</FormLabel>
						<TextField
							margin="normal"
							required
							fullWidth
							variant="standard"
							name="expiry"
							placeholder="123"
							InputProps={{
								inputComponent: StripeInputComponent,
								inputProps: {
									component: CardCvcElement,
									options: {
										placeholder: "123",
									},
								},
							}}
						/>
					</Box>
				</Box>
				<Typography variant="caption" color={caremeTheme.palette.grey[400]}>
					※セキュリティコードは、カード裏面の署名欄の上部に印字されている3桁または4桁の番号です
				</Typography>

				<Button
					type="submit"
					fullWidth
					variant="contained"
					sx={{ mt: 4, mb: 2 }}
					disabled={!stripe || submitting}
				>
					{submitting ? (
						<CircularProgress color="primary" />
					) : (
						buttonText || "無料でおためしする"
					)}
				</Button>
			</form>

			<Backdrop
				sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={submitting}
			>
				<Stack justifyContent="center" alignItems="center">
					<CircularProgress color="primary" />
					<Typography mt={2} color="white">
						購入処理中です...
					</Typography>
				</Stack>
			</Backdrop>
		</>
	);
}
